import React from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import StatTile from './StatTile'
import VoucherStateBadge from '../table/VoucherStateBadge'
import { formatPeriod } from '../../utils/utils'

const formatSek = (amount: number) =>
  new Intl.NumberFormat('en-SE', {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0,
  }).format(amount)

const monthLabel = (month: string) => {
  const [year, m] = month.split('-')
  const date = new Date(Number(year), Number(m) - 1, 1)
  return date.toLocaleDateString('en-US', { month: 'short' })
}

type PersonalViewProps = {
  awaitingMyApproval: number
  personal: {
    in_progress_count: number
    drafts_count: number
    paid_count: number
    paid_amount: number
    recent_vouchers: {
      id: string
      reference_number: number
      state: string
      total_amount: number
      expense_period?: number | null
      created_at?: string | number | null
    }[]
    monthly_volume: { month: string; count: number; total_amount: number }[]
  }
}

const PersonalView: React.FC<PersonalViewProps> = ({
  awaitingMyApproval,
  personal,
}) => {
  const tiles = [
    {
      label: 'In progress',
      value: personal.in_progress_count,
      caption:
        personal.in_progress_count > 0
          ? 'sent for approval, awaiting sign-off'
          : 'nothing pending',
      color:
        personal.in_progress_count > 0 ? 'warning.main' : 'success.main',
    },
    {
      label: 'Drafts to submit',
      value: personal.drafts_count,
      caption: personal.drafts_count > 0 ? 'started, not yet sent' : 'none right now',
    },
    {
      label: 'Paid this financial year',
      value: formatSek(personal.paid_amount),
      caption: `${personal.paid_count} vouchers`,
      color: 'success.main',
    },
  ]

  if (awaitingMyApproval > 0) {
    tiles.unshift({
      label: 'Awaiting your approval',
      value: awaitingMyApproval,
      caption: 'vouchers routed to you',
      color: 'warning.main',
    })
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tiles.map((tile) => (
          <Grid key={tile.label} size={{ xs: 12, sm: 6, md: 12 / tiles.length }}>
            <StatTile {...tile} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Your department's vouchers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Most recent activity
              </Typography>
              {personal.recent_vouchers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No vouchers yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Reference #</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">State</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {personal.recent_vouchers.map((v) => (
                      <TableRow key={v.id}>
                        <TableCell>#{v.reference_number}</TableCell>
                        <TableCell>
                          {v.expense_period
                            ? formatPeriod(v.expense_period)
                            : v.state === 'DRAFT'
                            ? 'Not yet submitted'
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {v.total_amount > 0 ? formatSek(v.total_amount) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <VoucherStateBadge state={v.state} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Your activity, last few months
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Vouchers created per month
              </Typography>
              <ResponsiveContainer width="100%" height={180}>
                <AreaChart
                  data={personal.monthly_volume.map((m) => ({
                    ...m,
                    label: monthLabel(m.month),
                  }))}
                  margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === 'count'
                        ? [value, 'Vouchers']
                        : [formatSek(value), 'Total']
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#0d6efd"
                    fill="#0d6efd"
                    fillOpacity={0.15}
                  />
                </AreaChart>
              </ResponsiveContainer>

              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Vouchers</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {personal.monthly_volume.map((m) => (
                    <TableRow key={m.month}>
                      <TableCell>{monthLabel(m.month)}</TableCell>
                      <TableCell align="right">{m.count}</TableCell>
                      <TableCell align="right">
                        {formatSek(m.total_amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PersonalView
